<script lang="ts">
  import { onMount } from "svelte";
  import { api, ApiError } from "../api";
  import type { AdminUser } from "../types";

  let { onClose }: { onClose: () => void } = $props();

  let users = $state<AdminUser[]>([]);
  let loading = $state(true);
  let error = $state<string | null>(null);

  let newUsername = $state("");
  let newPassword = $state("");
  let newRole = $state<"admin" | "user">("user");
  let creating = $state(false);

  let resetTargetId = $state<string | null>(null);
  let resetPassword = $state("");

  async function refresh(): Promise<void> {
    loading = true;
    try {
      const res = await api.adminListUsers();
      users = res.users;
    } catch (err) {
      error = err instanceof ApiError ? err.message : "Failed to load users";
    } finally {
      loading = false;
    }
  }

  onMount(refresh);

  async function createUser(event: SubmitEvent): Promise<void> {
    event.preventDefault();
    error = null;
    if (newPassword.length < 10) {
      error = "Password must be at least 10 characters";
      return;
    }
    creating = true;
    try {
      await api.adminCreateUser(newUsername.trim(), newPassword, newRole);
      newUsername = "";
      newPassword = "";
      newRole = "user";
      await refresh();
    } catch (err) {
      error = err instanceof ApiError ? err.message : "Failed to create user";
    } finally {
      creating = false;
    }
  }

  async function toggleActive(user: AdminUser): Promise<void> {
    try {
      await api.adminSetActive(user.id, !user.active);
      await refresh();
    } catch (err) {
      error = err instanceof ApiError ? err.message : "Failed to update user";
    }
  }

  function startReset(id: string): void {
    resetTargetId = id;
    resetPassword = "";
  }

  async function submitReset(event: SubmitEvent): Promise<void> {
    event.preventDefault();
    if (!resetTargetId) return;
    if (resetPassword.length < 10) {
      error = "Password must be at least 10 characters";
      return;
    }
    try {
      await api.adminResetPassword(resetTargetId, resetPassword);
      resetTargetId = null;
      resetPassword = "";
    } catch (err) {
      error = err instanceof ApiError ? err.message : "Failed to reset password";
    }
  }
</script>

<div class="page">
  <header>
    <button class="back" onclick={onClose} type="button" aria-label="Back">←</button>
    <h1>Admin — Users</h1>
  </header>

  <div class="content">
    {#if error}
      <p class="error">{error}</p>
    {/if}

    <form class="card" onsubmit={createUser}>
      <h2>Create user</h2>
      <label>
        Username
        <input type="text" bind:value={newUsername} maxlength="64" required />
      </label>
      <label>
        Initial password
        <input type="password" bind:value={newPassword} minlength="10" required />
      </label>
      <label>
        Role
        <select bind:value={newRole}>
          <option value="user">User</option>
          <option value="admin">Admin</option>
        </select>
      </label>
      <button type="submit" disabled={creating}>{creating ? "Creating…" : "Create user"}</button>
    </form>

    <h2 class="section-title">Users</h2>
    {#if loading}
      <p>Loading…</p>
    {:else}
      <ul class="users">
        {#each users as user (user.id)}
          <li class="card user-row">
            <div class="user-info">
              <strong>{user.username}</strong>
              <span class="muted">{user.role}{user.active ? "" : " · deactivated"}</span>
            </div>
            <div class="actions">
              <button type="button" onclick={() => startReset(user.id)}>Reset password</button>
              <button type="button" onclick={() => toggleActive(user)}>
                {user.active ? "Deactivate" : "Reactivate"}
              </button>
            </div>
            {#if resetTargetId === user.id}
              <form class="reset-form" onsubmit={submitReset}>
                <input
                  type="password"
                  bind:value={resetPassword}
                  placeholder="New password"
                  minlength="10"
                  required
                />
                <button type="submit">Save</button>
              </form>
            {/if}
          </li>
        {/each}
      </ul>
    {/if}
  </div>
</div>

<style>
  .page {
    display: flex;
    flex-direction: column;
    height: 100%;
  }
  header {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.75rem 1rem;
    padding-top: max(0.75rem, env(safe-area-inset-top));
    border-bottom: 1px solid var(--border);
  }
  .back {
    background: none;
    border: none;
    font-size: 1.3rem;
    cursor: pointer;
    color: var(--text);
  }
  h1 {
    margin: 0;
    font-size: 1.1rem;
  }
  .content {
    padding: 1rem;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }
  .card {
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    padding: 1rem;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    max-width: 420px;
  }
  h2 {
    margin: 0;
    font-size: 1rem;
  }
  .section-title {
    margin-top: 0.5rem;
  }
  label {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
    font-size: 0.9rem;
    color: var(--text-muted);
  }
  input,
  select {
    padding: 0.65rem;
    border-radius: var(--radius);
    border: 1px solid var(--border);
    background: var(--bg);
    color: var(--text);
    font-size: 1rem;
  }
  button[type="submit"] {
    padding: 0.75rem;
    border: none;
    border-radius: var(--radius);
    background: var(--accent);
    color: var(--accent-contrast);
    font-weight: 600;
    cursor: pointer;
  }
  .users {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }
  .user-row {
    flex-direction: row;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
  }
  .user-info {
    display: flex;
    flex-direction: column;
  }
  .muted {
    color: var(--text-muted);
    font-size: 0.85rem;
  }
  .actions {
    display: flex;
    gap: 0.5rem;
  }
  .actions button {
    border: 1px solid var(--border);
    background: var(--surface-alt);
    color: var(--text);
    border-radius: var(--radius);
    padding: 0.5rem 0.75rem;
    cursor: pointer;
    font-size: 0.85rem;
  }
  .reset-form {
    display: flex;
    gap: 0.5rem;
    width: 100%;
    margin-top: 0.5rem;
  }
  .reset-form input {
    flex: 1;
  }
  .reset-form button {
    border: none;
    border-radius: var(--radius);
    background: var(--accent);
    color: var(--accent-contrast);
    padding: 0 0.75rem;
    cursor: pointer;
  }
  .error {
    color: var(--danger);
  }
</style>
